/**
 * @namespace models
 */

/**
 * Entity cache class
 */

/**
 * Cache for entities
 *
 * @class EntityCacheFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.EntityCache} class
 *
 * @ngdoc factory
 * @name mwApp.factory:EntityCacheFactory
 */
/* @ngInject */
export default function EntityCacheFactory () {
  // use generic hasOwnProperty function
  var hasOwnProperty = Object.prototype.hasOwnProperty

  /**
   * EntityCache class
   *
   * @class EntityCache
   * @memberof models
   * @see models
   * @return {models.EntityCache}
   */
  function EntityCache () {
    this.cache = Object.create(null)
    this.unresolved_count = 0
  }
  EntityCache.prototype = {

    /**
     * Return unresolved value (Number actually)
     *
     * @memberof models.EntityCache#
     * @param  {Number} id   - entity id
     * @param  {String} type - entity type
     * @return {Number} new Number() object
     */
    unresolved: function (id, type) {
      this.unresolved_count++
      // we need to return Object, not primitive value, because constructors can return nothing except Objects
      var v = new Number(id) // eslint-disable-line no-new-wrappers
      v.entity_type = type
      return v
    },

    /**
     * Try to resolve unresolved links, if any
     *
     * @memberof models.EntityCache#
     * @param  {Object}   obj
     * @param  {models.Entity[]} [tree] - used for recursion
     */
    resolve: function (obj, tree) {
      tree = tree || [] // to avoid circular references
      tree.push(obj)

      var cache = this
      var checkInCache = function (obj, i) {
        if (obj[i] instanceof Number && obj[i].entity_type !== undefined) {
          var cachedObj = cache.get(+obj[i], obj[i].entity_type)
          if (cachedObj) {
            cache.unresolved_count--
            obj[i] = cachedObj
          }
        } else
        if (obj[i] !== null && typeof obj[i] === 'object' && tree.indexOf(obj[i]) === -1) {
          cache.resolve(obj[i], tree)
        }
      }

      if (Array.isArray(obj)) {
        for (var i = 0; i < obj.length; i++) {
          if (cache.unresolved_count === 0) {
            break
          }
          checkInCache(obj, i)
        }
      } else
      if (typeof obj === 'object') {
        for (var k in obj) {
          if (cache.unresolved_count === 0) {
            break
          }
          if (hasOwnProperty.call(obj, k)) {
            checkInCache(obj, k)
          }
        }
      }
    },

    /**
     * Put Entity to cache
     *
     * @memberof models.EntityCache#
     * @param  {Number} id - entity id
     * @param  {Object} obj - entity
     * @param  {String} [type=obj.constructor._name || 'Entity'] - entity type
     * @return {Boolean}
     */
    put: function (id, obj, type) {
      if (!id || !obj) {
        return false
      }
      type = type || obj.constructor._name || 'Entity'
      if (!this.cache[type]) {
        this.cache[type] = Object.create(null)
      }
      this.cache[type][id] = obj
      return true
    },

    /**
     * Get Entity from cache
     *
     * @memberof models.EntityCache#
     * @param  {Number} id - entity id
     * @param  {String} [type='Entity'] - entity type
     * @return {Object}
     */
    get: function (id, type) {
      if (id === undefined || id === null) {
        return null
      }
      type = type || 'Entity'
      if (!this.cache[type]) {
        return null
      }
      return this.cache[type][id]
    },

    /**
     * Check Entity in cache
     *
     * @memberof models.EntityCache#
     * @param  {Number} id - entity id
     * @param  {String} [type='Entity'] - entity type
     * @return {Boolean}
     */
    has: function (id, type) {
      return !!this.get(id, type)
    }

  }

  return EntityCache
}
